import { apiFetch } from "../../apiClient";
import { checkAuthStatus } from "../../utils/authStatus";
import { parseErrorResponse } from "../../utils/apiError";

export const YOUR_FILES_WEB_PATH = "/your-files";
export const SKILLS_WEB_PATH = "/skills";

function buildFinsterWebAppUrl(
  webPath: string,
  baseUrl?: string,
): string | undefined {
  const normalizedBaseUrl = baseUrl?.trim();
  if (!normalizedBaseUrl) return undefined;

  try {
    const baseWithTrailingSlash = normalizedBaseUrl.endsWith("/")
      ? normalizedBaseUrl
      : `${normalizedBaseUrl}/`;
    const url = new URL(webPath.replace(/^\//, ""), baseWithTrailingSlash);
    if (url.protocol !== "https:" && url.protocol !== "http:") {
      return undefined;
    }
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return undefined;
  }
}

export interface UserFile {
  id: string;
  name: string;
  status: string;
  createdAt: string;
  updatedAt?: string;
  path?: string;
  summary?: string;
  tags: string[];
  companyNames: string[];
  industry: string[];
}

export type YourFileUploadFailureReason =
  | "unsupported_file_type"
  | "file_too_large"
  | "bad_request"
  | "password_protected"
  | "provider_rejected"
  | "network_error";

const YOUR_FILE_UPLOAD_FAILURE_REASONS: ReadonlySet<string> = new Set<YourFileUploadFailureReason>([
  "unsupported_file_type",
  "file_too_large",
  "bad_request",
  "password_protected",
  "provider_rejected",
  "network_error",
]);

export interface UploadedYourFile {
  fileId: string;
  name: string;
}

export interface UploadYourFileOptions {
  /** Shared by every file chosen together so the batch can be traced. */
  requestId?: string;
  signal?: AbortSignal;
}

export class UserFilesApiError extends Error {
  readonly status: number;
  readonly reason?: YourFileUploadFailureReason;

  constructor(status: number, message: string, reason?: YourFileUploadFailureReason) {
    super(message);
    this.name = "UserFilesApiError";
    this.status = status;
    this.reason = reason;
  }
}

export function isQueryableUserFileStatus(status: string): boolean {
  const normalized = status.trim().toLowerCase();
  return normalized === "ready";
}

/**
 * Builds the outbound "manage Your Files" URL from a configured web-app base.
 *
 * The destination UI depends on `FINSTER_WEB_URL`: standalone Finster locally,
 * and the matching tenant Finster web-app host in deployed environments
 * (development uses st4 to match the backend). See
 * `DEPLOYMENT.md` for the matrix.
 */
export function buildYourFilesManagementUrl(baseUrl?: string): string | undefined {
  return buildFinsterWebAppUrl(YOUR_FILES_WEB_PATH, baseUrl);
}

/**
 * Builds the outbound "manage Skills" URL from a configured web-app base.
 *
 * Local uses standalone Finster. Deployed environments use the matching tenant
 * Finster web-app host (development and FactSet production: st4; staging:
 * st4-uat; FDS UBS: fds-prod-ubs). See `DEPLOYMENT.md`.
 */
export function buildSkillsManagementUrl(baseUrl?: string): string | undefined {
  return buildFinsterWebAppUrl(SKILLS_WEB_PATH, baseUrl);
}

export async function getUserFiles(signal?: AbortSignal): Promise<UserFile[]> {
  const response = await apiFetch("/api/finster/user-files", {
    method: "GET",
    signal,
  });

  checkAuthStatus(response);
  if (!response.ok) {
    throw new UserFilesApiError(response.status, await parseErrorResponse(response));
  }

  const payload = (await response.json()) as unknown;
  if (!Array.isArray(payload)) {
    throw new UserFilesApiError(502, "Your Files returned an invalid response.");
  }

  const isValidPayload = payload.every(
    (item): item is UserFile =>
      Boolean(
        item &&
          typeof item === "object" &&
          typeof (item as UserFile).id === "string" &&
          typeof (item as UserFile).name === "string" &&
          typeof (item as UserFile).status === "string" &&
          typeof (item as UserFile).createdAt === "string" &&
          Array.isArray((item as UserFile).tags) &&
          Array.isArray((item as UserFile).companyNames) &&
          Array.isArray((item as UserFile).industry)
      )
  );
  if (!isValidPayload) {
    throw new UserFilesApiError(502, "Your Files returned an invalid file record.");
  }
  return payload as UserFile[];
}

function readUploadFailureReason(payload: unknown): YourFileUploadFailureReason | undefined {
  const reason =
    payload && typeof payload === "object" ? (payload as { reason?: unknown }).reason : undefined;
  return typeof reason === "string" && YOUR_FILE_UPLOAD_FAILURE_REASONS.has(reason)
    ? (reason as YourFileUploadFailureReason)
    : undefined;
}

/**
 * Uploads one Your File under `storedName`. Resolves once the file is accepted
 * for scanning, before it is Ready; poll `getUserFiles` for its status.
 */
async function readJsonOrNull(response: Response, signal?: AbortSignal): Promise<unknown> {
  try {
    return await response.json();
  } catch (error) {
    if (signal?.aborted) throw error;
    return null;
  }
}

export async function uploadYourFile(
  file: File,
  storedName: string,
  { requestId, signal }: UploadYourFileOptions = {}
): Promise<UploadedYourFile> {
  const formData = new FormData();
  formData.append("file", file, storedName);
  formData.append("filename", storedName);
  if (requestId) formData.append("requestId", requestId);

  let response: Response;
  try {
    response = await apiFetch("/api/finster/user-files/upload", {
      method: "POST",
      body: formData,
      signal,
    });
  } catch (error) {
    if (signal?.aborted) throw error;
    throw new UserFilesApiError(0, "Your File upload failed.", "network_error");
  }

  checkAuthStatus(response);
  if (!response.ok) {
    const reason = readUploadFailureReason(await readJsonOrNull(response.clone(), signal));
    signal?.throwIfAborted();
    throw new UserFilesApiError(response.status, await parseErrorResponse(response), reason);
  }

  const payload = (await readJsonOrNull(response, signal)) as {
    file?: { fileId?: unknown; name?: unknown };
  } | null;
  const fileId = payload?.file?.fileId;
  const name = payload?.file?.name;
  if (typeof fileId !== "string" || !fileId || typeof name !== "string") {
    throw new UserFilesApiError(502, "Your File upload returned an invalid response.");
  }
  return { fileId, name };
}
